import { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input, { Select } from '../../components/ui/Input';
import Table, { THead, TH, TBody, TR, TD } from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import toast from 'react-hot-toast';

const TYPE_COLORS = { government: 'blue', institutional: 'red', law_enforcement: 'purple', enterprise: 'cyan' };

export default function ManageOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', shortName: '', type: 'government', country: '', contactName: '', contactEmail: '', address: '' });
  const [creating, setCreating] = useState(false);

  const loadOrgs = async () => {
    try {
      const { data } = await api.get('/admin/organizations');
      setOrgs(data.organizations);
    } catch (err) {
      console.error('Load orgs error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOrgs(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/organizations', form);
      toast.success('Organization created');
      setShowCreate(false);
      setForm({ name: '', shortName: '', type: 'government', country: '', contactName: '', contactEmail: '', address: '' });
      loadOrgs();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Organizations</h1>
          <p className="text-slate-400 mt-1">Manage client organizations</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>+ New Organization</Button>
      </div>

      <Table>
        <THead>
          <tr><TH>Name</TH><TH>Type</TH><TH>Country</TH><TH>Contact</TH><TH>Users</TH><TH>Appliances</TH><TH>Certificates</TH></tr>
        </THead>
        <TBody>
          {orgs.map(o => (
            <TR key={o.id}>
              <TD>
                <p className="text-slate-200 font-medium">{o.shortName || o.name}</p>
                {o.shortName && <p className="text-xs text-slate-400">{o.name}</p>}
              </TD>
              <TD><Badge color={TYPE_COLORS[o.type]}>{o.type.replace('_', ' ')}</Badge></TD>
              <TD>{o.country}</TD>
              <TD><span className="text-xs text-slate-400">{o.contactEmail || '—'}</span></TD>
              <TD>{o._count?.users || 0}</TD>
              <TD>{o._count?.appliances || 0}</TD>
              <TD>{o._count?.certificates || 0}</TD>
            </TR>
          ))}
        </TBody>
      </Table>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Organization" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Indonesian National Police" />
          <Input label="Short Name" value={form.shortName} onChange={(e) => setForm({ ...form, shortName: e.target.value })} placeholder="e.g., POLRI" />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="government">Government</option>
              <option value="institutional">Institutional</option>
              <option value="law_enforcement">Law Enforcement</option>
              <option value="enterprise">Enterprise</option>
            </Select>
            <Input label="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required placeholder="e.g., Indonesia" />
          </div>
          <Input label="Contact Name" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
          <Input label="Contact Email" type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
          <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={creating}>{creating ? 'Creating...' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
